import * as React from 'react';
import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import ApiService from '../../Services/ApiService';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';
import { ConstCenterRecord, CostCenterCustomer, CostCenterModifiedUser } from './types';
import {
  COST_CENTERS_CREATED_AT_KEY,
  COST_CENTERS_CREATED_BY_KEY,
  COST_CENTERS_CUSTOMER_KEY,
  COST_CENTERS_DELETE_KEY,
  COST_CENTERS_DETAIL_KEY,
  COST_CENTERS_MODIFIED_AT_KEY,
  COST_CENTERS_MODIFIED_BY_KEY,
  COST_CENTERS_NAME_KEY,
  COST_CENTERS_NOTE_KEY,
  COST_CENTERS_TABLE_KEY,
} from './constants';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import TextCell from '../CmdTable/TableCells/TextCell';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import DetailAction from '../CmdTable/TableActions/DetailAction';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import RemoveAction from './RemoveAction';
import { URL_CODEBOOKS, URL_COST_CENTERS } from '../../router/constants';
import ThText from '../CmdTable/TableHeadCell/ThText';
import { NOT_AVAILABLE_VALUE } from '../CmdTable/constants';
import { TABLE_COST_CENTERS } from '../../Services/TableService';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';

type UseCostCenters = {
  columns: ColumnDef<ConstCenterRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
};

export default function useConstCenters(): UseCostCenters {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(COST_CENTERS_TABLE_KEY)
  );
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(COST_CENTERS_TABLE_KEY)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [COST_CENTERS_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const handleRemove = React.useCallback(
    async (data: ConstCenterRecord) => {
      const response = await ApiService.delete(
        `/v1/${TABLE_COST_CENTERS}/${data.id}`,
        // `/v1/${COST_CENTERS_TABLE_KEY}/${data.id}/remove?customerId=${data.customer.id}`,
        { id: data.id },
        true
      );
      if (response.data && response.status === 200) {
        updateTable();
      }
    },
    [updateTable]
  );

  const handleAction = (id: number, customerId?: number | number[]) => {
    if (customerId && !Array.isArray(customerId)) {
      navigate(`${URL_CODEBOOKS}${URL_COST_CENTERS}/edit/${id}?customerId=${customerId}`);
    }
  };

  const columns = React.useMemo<ColumnDef<ConstCenterRecord>[]>(
    () => [
      {
        accessorKey: COST_CENTERS_NAME_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThSearch
            tableName={COST_CENTERS_TABLE_KEY}
            id={COST_CENTERS_NAME_KEY}
            title={f({ id: `table_list.columns.name` })}
            isSortable={true}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.name ? <TextCell text={info.getValue<string>()} /> : null,
        minSize: 150,
      },
      {
        accessorKey: COST_CENTERS_CUSTOMER_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThSimpleSelect
            tableName={COST_CENTERS_TABLE_KEY}
            id={COST_CENTERS_CUSTOMER_KEY}
            title={f({
              id: `table_list.columns.customer`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.[COST_CENTERS_CUSTOMER_KEY] ? (
            <CustomerCell customer={info.getValue<CostCenterCustomer>()} />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 260,
      },
      {
        accessorKey: COST_CENTERS_NOTE_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThText
            title={f({
              id: `table_list.columns.note`,
            })}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.note ? (
            <TextCell
              text={info.getValue<string>()}
              tooltipEnabled={info.getValue<string>() !== NOT_AVAILABLE_VALUE}
            />
          ) : null,
        minSize: 180,
      },
      {
        accessorKey: COST_CENTERS_CREATED_BY_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThMultiSelect
            tableName={COST_CENTERS_TABLE_KEY}
            id={COST_CENTERS_CREATED_BY_KEY}
            title={f({ id: `table_list.columns.createdBy` })}
            tooltip={f({ id: `table_list.columns.createdBy.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<ConstCenterRecord>().name || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: COST_CENTERS_CREATED_AT_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThDateTime
            tableName={COST_CENTERS_TABLE_KEY}
            id={COST_CENTERS_CREATED_AT_KEY}
            title={f({ id: `table_list.columns.createdAt` })}
            tooltip={f({ id: `table_list.columns.createdAt.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: COST_CENTERS_MODIFIED_BY_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThMultiSelect
            tableName={COST_CENTERS_TABLE_KEY}
            id={COST_CENTERS_MODIFIED_BY_KEY}
            title={f({ id: `table_list.columns.modifiedBy` })}
            tooltip={f({ id: `table_list.columns.modifiedBy.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<CostCenterModifiedUser>().name || '[N/A]'} />
          ) : null,
        minSize: 260,
      },
      {
        accessorKey: COST_CENTERS_MODIFIED_AT_KEY,
        header: (_table: HeaderContext<ConstCenterRecord, unknown>) => (
          <ThDateTime
            id={COST_CENTERS_MODIFIED_AT_KEY}
            tableName={COST_CENTERS_TABLE_KEY}
            title={f({ id: `table_list.columns.modifiedAt` })}
            tooltip={f({ id: `table_list.columns.modifiedAt.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original?.modifiedAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} />
          ) : null,
        minSize: 260,
      },

      {
        accessorKey: COST_CENTERS_DETAIL_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail p-2">
            {f({ id: 'table.detailBtn' })}
          </div>
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original && (
            <DetailAction
              id={info.row.original.id}
              tableName={TABLE_COST_CENTERS}
              customerId={info.row.original.customer.id}
              onClickCallback={handleAction}
            />
          ),
        size: 60, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: COST_CENTERS_DELETE_KEY,
        header: () => (
          <div className="w-sm text-center th-sticky sticky-col detail p-2">
            {f({ id: 'table.deleteBtn' })}
          </div>
        ),
        cell: (info: CellContext<ConstCenterRecord, unknown>) =>
          info.row.original ? (
            <RemoveAction data={info.row.original} handleRemove={handleRemove} />
          ) : null,
        size: 100, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleRemove]
  );

  const columnOrder = React.useMemo(() => {
    const order = columnsTable.map((item) => item.key);
    // add the detail and delete columns to the end
    order.push(COST_CENTERS_DETAIL_KEY, COST_CENTERS_DELETE_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility: { [index: string]: boolean } = React.useMemo(() => {
    return columnsTable.reduce((acc: { [index: string]: boolean }, column: ICmdTableColumn) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
